// Module-level singleton for CloudWatch RUM. RumInit constructs the AwsRum
// instance at startup and registers it here so error boundaries (error.tsx,
// global-error.tsx) can report React render errors — which never bubble to
// window.onerror and would otherwise be invisible to RUM.

type AwsRumLike = {
  recordError: (error: Error) => void;
};

let rumInstance: AwsRumLike | null = null;

// ── Rate limiter ─────────────────────────────────────────────────────
// Prevents runaway error loops (bad useEffect deps, render storms) from
// burning RUM budget. AWS RUM has no native dedupe; without this, a tight
// render loop calling console.error could send tens of thousands of events
// per minute at $1/100k. Two layers:
//
//   1. Per-key dedupe — first 5 occurrences of each (message + top stack
//      frame) within a 60s window are recorded; dropped after.
//   2. Hard cap — max 100 events across ALL keys per 60s window, as a
//      backstop against many distinct errors firing simultaneously.
//
// State is cleared every 5 minutes so the key cache can't grow unbounded.

const DEDUPE_WINDOW_MS = 60_000;
const MAX_PER_KEY = 5;
const HARD_CAP_WINDOW_MS = 60_000;
const HARD_CAP_MAX = 100;
const STATE_CLEAR_INTERVAL_MS = 5 * 60_000;

interface DedupeEntry {
  count: number;
  windowStart: number;
}

const dedupeState = new Map<string, DedupeEntry>();
let hardCapCount = 0;
let hardCapWindowStart = 0;

// Periodic clear of the dedupe cache. Window flag guards against double
// registration under Next.js dev hot reload.
if (typeof window !== "undefined") {
  const w = window as Window & { __rumStateIntervalSet?: boolean };
  if (!w.__rumStateIntervalSet) {
    w.__rumStateIntervalSet = true;
    setInterval(() => {
      dedupeState.clear();
    }, STATE_CLEAR_INTERVAL_MS);
  }
}

function shouldReportError(err: Error): boolean {
  const now = Date.now();

  // Hard cap: rotate the window if expired, then gate-keep on total count.
  if (now - hardCapWindowStart > HARD_CAP_WINDOW_MS) {
    hardCapWindowStart = now;
    hardCapCount = 0;
  }
  if (hardCapCount >= HARD_CAP_MAX) return false;

  // Per-key dedupe: first 5 occurrences per key per window pass through.
  // If the map was cleared (5-minute periodic wipe), the key is absent and
  // this counts as a fresh first-appearance — always records.
  const key = `${err.message}|${(err.stack ?? "").split("\n")[1] ?? ""}`;
  const entry = dedupeState.get(key);
  if (!entry || now - entry.windowStart > DEDUPE_WINDOW_MS) {
    dedupeState.set(key, { count: 1, windowStart: now });
    hardCapCount += 1;
    return true;
  }
  if (entry.count < MAX_PER_KEY) {
    entry.count += 1;
    hardCapCount += 1;
    return true;
  }
  return false;
}

// ── Public API ───────────────────────────────────────────────────────

export function setRumInstance(instance: AwsRumLike): void {
  rumInstance = instance;
}

export function reportErrorToRum(error: unknown): void {
  if (!rumInstance) return;
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    if (!shouldReportError(err)) return;
    rumInstance.recordError(err);
  } catch (e) {
    // Use console.debug (not .error) so we don't cascade through any
    // upstream error capture that might call us back.
    console.debug("[rum] recordError failed in reportErrorToRum", e);
  }
}

// Monkey-patches console.error so the error logs we ship from try/catch
// sites across the app flow into RUM without a refactor. Sentry and PostHog
// do the same via their own integrations; all three layers coexist by each
// preserving the prior console.error in closure.
//
// Guards against double-patching under dev hot reload.
export function patchConsoleErrorForRum(): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { __rumConsolePatched?: boolean };
  if (w.__rumConsolePatched) return;
  w.__rumConsolePatched = true;

  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    originalError.apply(console, args);
    try {
      if (!rumInstance) return;
      const err =
        args.find((a): a is Error => a instanceof Error) ??
        new Error(args.map(stringify).join(" "));
      if (!shouldReportError(err)) return;
      rumInstance.recordError(err);
    } catch (e) {
      // console.debug specifically — calling console.error here would
      // re-enter this very patched function and could recurse.
      console.debug("[rum] recordError failed inside patched console.error", e);
    }
  };
}

function stringify(a: unknown): string {
  if (typeof a === "string") return a;
  try {
    return JSON.stringify(a);
  } catch (e) {
    // Circular ref or similar. Fall back to String() which never throws.
    console.debug("[rum] JSON.stringify fallback", e);
    return String(a);
  }
}
